<template>
    <div>
        <v-row v-if="!isReadOnly" dense>
            <v-col cols="12" sm="6" md="4">
                <v-file-input
                    :key="fileInputKey"
                    v-model="filesArray"
                    prepend-icon="mdi-camera"
                    small-chips
                    variant="outlined"
                    density="compact"
                    accept="image/jpg, image/jpeg, image/png"
                    class="cursor-pointer"
                    multiple
                    :label="$t('select_photo')"
                    @update:model-value="onPhotoLoad"
                />
            </v-col>
        </v-row>
        <v-row class="pb-6">
            <v-col v-for="(image, i) in images" :key="image.id" cols="12" :md="12 / imagesInARow">
                <slot name="top-fields" :image="image" />
                <v-card class="mb-5">
                    <div style="position: relative">
                        <div :style="{opacity: image.toDelete ? 0.3 : 1}">
                            <v-img
                                :src="image.fileStream || $filters.imageUrl(image.url, 300)"
                                :lazy-src="$filters.srcHost('/placeholder.png')"
                                :aspect-ratio="16 / 9"
                                type="number"
                                gradient="to bottom, rgba(0,0,0,.05), rgba(0,0,0,.1)"
                                class="bg-grey-lighten-2 mb-8"
                            />
                        </div>
                        <v-btn
                            v-if="!isReadOnly"
                            :icon="image.toDelete ? 'mdi-undo' : 'mdi-delete'"
                            variant="text"
                            style="position: absolute; right: 4px; top: 4px"
                            @click="toggleDeleteImage(image)"
                        />
                        <v-icon
                            v-if="image.toDelete"
                            :color="'error'"
                            style="
                                position: absolute;
                                left: 0;
                                right: 0;
                                top: 0;
                                bottom: 0;
                                width: 24px;
                                height: 24px;
                                margin: auto;
                            "
                        >
                            mdi-close
                        </v-icon>
                    </div>
                </v-card>
                <slot name="fields" :image="image" />
                <v-number-input
                    v-if="orderEditable"
                    v-model="image.order"
                    control-variant="stacked"
                    :label="$t('display_number')"
                    :min="1"
                    :max="images?.length"
                    :step="1"
                    density="compact"
                    :data-cy="`image-${image.order}`"
                    variant="outlined"
                    hide-details
                    :readonly="isReadOnly"
                    :disabled="image.toDelete"
                    @update:model-value="onImageDisplayNumberChange($event, image, i)"
                />
                <slot name="bottom-fields" :image="image" />
            </v-col>
        </v-row>
    </div>
</template>

<script lang="ts">
    import {Component, Emit, Prop, VModel, Vue} from 'vue-facing-decorator'
    import {blobToBase64, clone} from '~/utils/helpers'
    import {nanoid} from 'nanoid'

    @Component
    export class ImagesUploadForm extends Vue {
        @VModel() images
        @Prop({default: 4}) imagesInARow
        @Prop() initData
        @Prop({default: true}) orderEditable

        @Emit()
        deleteImage(img) {
            return img
        }

        fileInputKey = 0
        filesArray = []
        isHotelDescriptionMode: boolean = inject('isHotelDescriptionMode', false)

        async onPhotoLoad() {
            for (let i = 0; i < this.filesArray.length; i++) {
                const base64Img = await blobToBase64(this.filesArray[i])

                this.images.push({
                    ...(this.initData && clone(this.initData)),
                    order: this.images.length + 1,
                    fileStream: base64Img,
                    toDelete: false,
                    blob: this.filesArray[i],
                    id: nanoid(),
                })
            }
            this.filesArray = []
        }

        toggleDeleteImage(image) {
            image.toDelete = !image.toDelete
            if (image.toDelete) this.deleteImage(image)
        }

        onImageDisplayNumberChange(event, image, index) {
            if (!isNaN(event)) {
                const newImgDisplayNumber = +event
                const prevImgDisplayNumber = index + 1

                this.images.forEach(img => {
                    if (newImgDisplayNumber < prevImgDisplayNumber) {
                        if (
                            +img.order >= newImgDisplayNumber &&
                            img !== image &&
                            !(+img.order > prevImgDisplayNumber)
                        ) {
                            img.order = +img.order + 1
                        }
                    } else {
                        if (
                            +img.order <= newImgDisplayNumber &&
                            img !== image &&
                            !(+img.order < prevImgDisplayNumber)
                        ) {
                            img.order = +img.order - 1
                        }
                    }
                })

                this.images.sort((img1, img2) => {
                    return img1.order - img2.order
                })
            } else {
                index
                image.order = index + 1
            }
        }

        get isReadOnly() {
            if (this.isHotelDescriptionMode && this.$conf.public.account.allowToEditExternalHotelDescription) {
                return false
            }

            return this.isHotelDescriptionMode
        }
    }

    export default ImagesUploadForm
</script>
